import React from 'react'
import PropTypes from 'prop-types'
import { Block, Icon } from '@qure/containers'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStoreState } from 'pullstate'
import _ from 'lodash'

import UIStore from 'states/globalStore'
import mixPanelEventsConstants from 'libs/mixPanelEventsConstants'
import useMixPanelTracking from 'libs/MixpanelIntegration'
import backendChecks from 'libs/backendChecks'
import getFeatureToggles from 'libs/getFeatureToggles'

import UserSettings from './components/UserSettings'
import SiteSelector from '../SiteSelector'

import './index.scss'

const Sidebar = props => {
  const { onLogout } = props

  const { userProfile } = useStoreState(UIStore, s => ({
    userProfile: s.userProfile
  }))
  const { t } = useTranslation()
  const {
    hasFeedback,
    hasTests,
    hasDicomUpload,
    hasPngUpload
  } = getFeatureToggles(userProfile)

  const mixPanel = useMixPanelTracking()

  const onTrackLink = e => {
    const dataMenuItem = _.get(e, ['target', 'dataset', 'menuItem'])
    mixPanel.track(dataMenuItem, { menuItem: dataMenuItem })
  }

  const roles = backendChecks.getAllRoles(userProfile)

  return (
    <div className="sidebar-container">
      <div className="side-top-wrapper mb-4">
        <Block>
          <div className="logo-wrapper">
            <img src="/qTrack_logo.svg" alt="qTrack logo" />
          </div>
        </Block>
        <Block>
          <div className="site-wrapper">
            <SiteSelector />
          </div>
        </Block>
      </div>
      <div className="menu-wrapper">
        <aside className="menu">
          <GeneralMenu userProfile={userProfile} onTrackLink={onTrackLink} />
          <CreationMenu
            roles={roles}
            userProfile={userProfile}
            onTrackLink={onTrackLink}
            hasDicomUpload={hasDicomUpload}
            hasPngUpload={hasPngUpload}
          />
          <RadiologistResultMenu
            roles={roles}
            hasFeedback={hasFeedback}
            onTrackLink={onTrackLink}
          />
          <LabResultMenu
            roles={roles}
            hasTests={hasTests}
            onTrackLink={onTrackLink}
          />
          <ManageMenu roles={roles} onTrackLink={onTrackLink} />
        </aside>
      </div>
      <div className="bottom-wrapper">
        <UserSettings userProfile={userProfile} onLogout={onLogout} t={t} />
      </div>
    </div>
  )
}

Sidebar.defaultProps = {}

Sidebar.propTypes = {
  onLogout: PropTypes.func.isRequired
}

const GeneralMenu = props => {
  const { userProfile, onTrackLink } = props
  const { t } = useTranslation()
  return (
    <>
      <p className="menu-label">{t('General')}</p>
      <ul className="menu-list">
        <li>
          <NavLink
            to="/"
            exact
            onClick={onTrackLink}
            activeClassName="is-active"
            data-menu-item={mixPanelEventsConstants.DASHBOARD}
            className="is-flex is-align-items-center"
          >
            <Icon iConClass="fa-tachometer-alt" />
            {t('Dashboard')}
          </NavLink>
        </li>

        <li>
          <NavLink
            to="/client-lists"
            exact
            data-menu-item={mixPanelEventsConstants.VIEW_CLIENT_LIST}
            activeClassName="is-active"
            onClick={onTrackLink}
            className="is-flex is-align-items-center"
          >
            <Icon iConClass="fa-list-alt" />
            {backendChecks.translateClientName(
              'View client list',
              userProfile,
              t
            )}
          </NavLink>
        </li>
      </ul>
    </>
  )
}

GeneralMenu.propTypes = {
  userProfile: PropTypes.object.isRequired,
  onTrackLink: PropTypes.func.isRequired
}

const CreationMenu = props => {
  const {
    roles,
    hasDicomUpload,
    hasPngUpload,
    userProfile,
    onTrackLink
  } = props
  const { t } = useTranslation()

  if (roles.hasHealthWorkerRole) {
    return (
      <>
        <p className="menu-label">{t('Registration')}</p>
        <ul className="menu-list">
          <li>
            <NavLink
              to="/add-client"
              exact
              activeClassName="is-active"
              onClick={onTrackLink}
              data-menu-item={mixPanelEventsConstants.ADD_NEW_CLIENT}
              className="is-flex is-align-items-center"
            >
              <Icon iConClass="fa-user-plus" />
              {backendChecks.translateClientName(
                'Add new client',
                userProfile,
                t
              )}
            </NavLink>
          </li>
          {(hasPngUpload || hasDicomUpload) && (
            <li>
              <NavLink
                to="/uploads"
                exact
                data-menu-item={mixPanelEventsConstants.MANAGE_UPLOADS}
                onClick={onTrackLink}
                activeClassName="is-active"
                className="is-flex is-align-items-center"
              >
                <Icon iConClass="fa-cloud-upload-alt" />
                {t('Manage uploads')}
              </NavLink>
            </li>
          )}
        </ul>
      </>
    )
  }

  return null
}

CreationMenu.defaultProps = {
  roles: {}
}

CreationMenu.propTypes = {
  roles: PropTypes.object,
  hasDicomUpload: PropTypes.bool.isRequired,
  hasPngUpload: PropTypes.bool.isRequired,
  userProfile: PropTypes.object.isRequired,
  onTrackLink: PropTypes.func.isRequired
}

const ManageMenu = props => {
  const { roles, onTrackLink } = props
  const { t } = useTranslation()

  if (roles.hasAdminRole) {
    return (
      <>
        <p className="menu-label">{t('Administration')}</p>
        <ul className="menu-list">
          <li>
            <NavLink
              to="/manage-users"
              exact
              activeClassName="is-active"
              onClick={onTrackLink}
              data-menu-item={mixPanelEventsConstants.MANAGE_USERS}
              className="is-flex is-align-items-center"
            >
              <Icon iConClass="fa-users-cog" />
              {t('Manage users')}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/manage-sites"
              exact
              activeClassName="is-active"
              onClick={onTrackLink}
              data-menu-item={mixPanelEventsConstants.MANAGE_SITES}
              className="is-flex is-align-items-center"
            >
              <Icon iConClass="fa-thumbtack" />
              {t('Manage sites')}
            </NavLink>
          </li>
        </ul>
      </>
    )
  }

  return null
}

ManageMenu.defaultProps = {
  roles: {}
}

ManageMenu.propTypes = {
  roles: PropTypes.object,
  onTrackLink: PropTypes.func.isRequired
}

const RadiologistResultMenu = props => {
  const { roles, hasFeedback, onTrackLink } = props
  const { t } = useTranslation()

  if (hasFeedback && roles.hasRadiologistRole) {
    return (
      <>
        <p className="menu-label">{t('Radiology')}</p>
        <ul className="menu-list">
          <li>
            <NavLink
              to="/radiologist-lists"
              exact
              activeClassName="is-active"
              onClick={onTrackLink}
              data-menu-item={mixPanelEventsConstants.VIEW_RADIOLOGIST_FEEDBACK}
              className="is-flex is-align-items-center"
            >
              <Icon iConClass="fa-list-alt" />
              {t('View Radiologist feedback')}
            </NavLink>
          </li>
        </ul>
      </>
    )
  }

  return null
}

RadiologistResultMenu.defaultProps = {
  roles: {},
  hasFeedback: false
}

RadiologistResultMenu.propTypes = {
  roles: PropTypes.object,
  hasFeedback: PropTypes.bool,
  onTrackLink: PropTypes.func.isRequired
}

const LabResultMenu = props => {
  const { roles, hasTests, onTrackLink } = props
  const { t } = useTranslation()

  if (hasTests && roles.hasHealthWorkerRole) {
    return (
      <>
        <p className="menu-label">{t('Lab Results')}</p>
        <ul className="menu-list">
          <li>
            <NavLink
              to="/tests-lists"
              exact
              activeClassName="is-active"
              onClick={onTrackLink}
              data-menu-item={mixPanelEventsConstants.VIEW_LAB_CONFIRMATIONS}
              className="is-flex is-align-items-center"
            >
              <Icon iConClass="fa-list-alt" />
              {t('View lab confirmations')}
            </NavLink>
          </li>
        </ul>
      </>
    )
  }

  return null
}

LabResultMenu.defaultProps = {
  roles: {},
  hasTests: false
}

LabResultMenu.propTypes = {
  roles: PropTypes.object,
  hasTests: PropTypes.bool,
  onTrackLink: PropTypes.func.isRequired
}

export default Sidebar
